<template>
  <div class="g-page">
    <div class="content-container">
      <!-- HEADER -->
      <div class="header-row">
        <h2 class="page-title">{{ $t("Ta'minotchilar Bazasi") }}</h2>
        <div class="header-actions">
          <div class="search-wrapper">
            <b-icon icon="search" class="search-icon"></b-icon>
            <input v-model="filter" type="search" :placeholder="$t('Qidiruv...')" class="modern-search">
          </div>
          <button class="refresh-btn" @click="Data()" :title="$t('Yangilash')">
            <b-icon icon="arrow-repeat"></b-icon>
          </button>
          <button class="add-btn" @click="$router.push('/malumot/taminotchi/create')" v-if="rule.change">
            <b-icon icon="plus-circle-fill"></b-icon> {{ $t("Qo'shish") }}
          </button>
        </div>
      </div>

      <!-- BALANCE FILTER ROW -->
      <div class="balance-filter-row">
        <button :class="['bf-btn', { active: balanceFilter === '' }]" @click="setBalanceFilter('')">
          📋 Hammasi
        </button>
        <button :class="['bf-btn', { active: balanceFilter === 'qarzdor' }]" @click="setBalanceFilter('qarzdor')">
          🔴 Biz qarzdor
        </button>
        <button :class="['bf-btn', { active: balanceFilter === 'haqdor' }]" @click="setBalanceFilter('haqdor')">
          🟢 Avans (haqdor)
        </button>
        <button :class="['bf-btn', { active: balanceFilter === 'tenglashgan' }]" @click="setBalanceFilter('tenglashgan')">
          ⚪ Tenglik
        </button>

        <!-- Jami summary (supplier kontekstida):
             qarzdor_total (balance > 0) = BIZ QARZDORMIZ (prixod, hali to'lanmagan)
             haqdor_total  (balance < 0) = BIZGA QAYTADI (avans, supplier qaytaradi) -->
        <div class="balance-summary" v-if="totals">
          <div class="bs-cell" v-if="totals.qarzdor_total > 0">
            <span class="bs-label">Biz so'mda qarzdormiz:</span>
            <strong class="bs-positive">−{{ formatMoney(totals.qarzdor_total) }}</strong>
          </div>
          <div class="bs-cell" v-if="totals.qarzdor_total_dollar > 0">
            <span class="bs-label">Biz $da qarzdormiz:</span>
            <strong class="bs-positive">−{{ formatDollar(totals.qarzdor_total_dollar) }}</strong>
          </div>
          <div class="bs-cell" v-if="totals.haqdor_total > 0">
            <span class="bs-label">Bizga qaytarilishi (so'm):</span>
            <strong class="bs-negative">+{{ formatMoney(totals.haqdor_total) }}</strong>
          </div>
          <div class="bs-cell" v-if="totals.haqdor_total_dollar > 0">
            <span class="bs-label">Bizga qaytarilishi ($):</span>
            <strong class="bs-negative">+{{ formatDollar(totals.haqdor_total_dollar) }}</strong>
          </div>
        </div>
      </div>

      <!-- TABLE -->
      <div class="table-card">
        <div class="table-wrapper">
          <table class="custom-table">
            <thead>
              <tr>
                <th style="width: 50px">#</th>
                <th>{{ $t("TA'MINOTCHI") }}</th>
                <th>{{ $t('TEL') }}</th>
                <th class="num">SO'MDA QARZ</th>
                <th class="num">DOLLARDA QARZ</th>
                <th style="width: 130px; text-align: center;">HOLAT</th>
                <th style="width: 140px; text-align: center;">{{ $t('AMALLAR') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in filteredRows" :key="item.id" class="clickable-row" @click="openHistory(item)" @dblclick="handleRowClick(item)">
                <td><span class="id-badge">{{ index + 1 }}</span></td>
                <td>
                  <div class="font-bold">{{ item.name }}</div>
                  <div class="text-muted small" v-if="item.address">{{ item.address }}</div>
                </td>
                <td>{{ item.phone_number || '—' }}</td>

                <!-- SUM balansi -->
                <!-- Supplier konteksti: balance > 0 = biz qarzdormiz (qizil), balance < 0 = avans (yashil) -->
                <td class="num">
                  <span v-if="item.balance > 0" class="bal-badge bal-debt">
                    {{ formatMoney(item.balance_abs) }}
                  </span>
                  <span v-else-if="item.balance < 0" class="bal-badge bal-credit">
                    +{{ formatMoney(item.balance_abs) }}
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>

                <!-- DOLLAR balansi -->
                <td class="num">
                  <span v-if="item.balance_dollar > 0" class="bal-badge bal-debt">
                    {{ formatDollar(item.balance_dollar_abs) }}
                  </span>
                  <span v-else-if="item.balance_dollar < 0" class="bal-badge bal-credit">
                    +{{ formatDollar(item.balance_dollar_abs) }}
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>

                <td class="text-center">
                  <span :class="['status-pill', overallStatus(item)]">
                    {{ overallStatusLabel(item) }}
                  </span>
                </td>
                <td>
                  <div class="actions">
                    <button class="icon-btn pay" @click.stop="openPayModal(item)" title="To'lov qilish">
                      <b-icon icon="cash-stack"></b-icon>
                    </button>
                    <button class="icon-btn edit" @click.stop="$router.push('/malumot/taminotchi/update/' + item.id)" title="Tahrirlash">
                      <b-icon icon="pencil-square"></b-icon>
                    </button>
                    <button class="icon-btn del" @click.stop="trash(item.id)" v-if="rule.change" title="O'chirish">
                      <b-icon icon="trash"></b-icon>
                    </button>
                  </div>
                </td>
              </tr>

              <tr v-if="loading">
                <td colspan="7" class="empty-row"><div class="spinner"></div> Yuklanmoqda...</td>
              </tr>
              <tr v-if="!loading && filteredRows.length === 0">
                <td colspan="7" class="empty-row">{{ $t("Ma'lumot topilmadi") }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="table-footer">
          <div class="info">
            Jami: <b>{{ filteredRows.length }}</b> / <b>{{ totalCount }}</b> ta ta'minotchi
          </div>
          <div v-if="hasMore">
            <button class="btn-load-more" @click="loadMore">Yana 50 ta yuklash</button>
          </div>
        </div>
      </div>
    </div>

    <!-- TO'LOV MODAL -->
    <b-modal id="modal-pay-supplier" hide-footer size="md" modal-class="modern-modal">
      <template #modal-title>
        <span v-if="payTarget">
          <b-icon icon="cash-stack"></b-icon>
          {{ payTarget.name }} — to'lov qilish
        </span>
      </template>

      <div v-if="payTarget" class="pay-content">
        <!-- KASSA BALANSI -->
        <div v-if="kassaBalance" class="kassa-row">
          <div class="kr-title">💼 KASSA hozir:</div>
          <div class="kr-cells">
            <div v-if="kassaBalance.sum_total !== 0" class="kr-cell">
              <span class="kr-label">So'm</span>
              <strong>{{ formatMoney(kassaBalance.sum_total) }}</strong>
            </div>
            <div v-if="kassaBalance.dollar_total !== 0" class="kr-cell dollar">
              <span class="kr-label">USD</span>
              <strong>{{ formatDollar(kassaBalance.dollar_total) }}</strong>
            </div>
          </div>
        </div>

        <!-- Joriy balans (supplier: balance > 0 = biz qarzdormiz / negative ko'rinadi) -->
        <div class="pay-balance-row">
          <div class="pb-cell" v-if="payTarget.balance !== 0">
            <span class="pb-label">SO'M qarz (biz):</span>
            <strong :class="payTarget.balance > 0 ? 'negative' : 'positive'">
              {{ payTarget.balance > 0 ? '−' : '+' }}{{ formatMoney(payTarget.balance_abs) }}
            </strong>
          </div>
          <div class="pb-cell" v-if="payTarget.balance_dollar !== 0">
            <span class="pb-label">DOLLAR qarz (biz):</span>
            <strong :class="payTarget.balance_dollar > 0 ? 'negative' : 'positive'">
              {{ payTarget.balance_dollar > 0 ? '−' : '+' }}{{ formatDollar(payTarget.balance_dollar_abs) }}
            </strong>
          </div>
          <div class="pb-cell" v-if="payTarget.balance === 0 && payTarget.balance_dollar === 0">
            <span class="pb-label">Holat:</span>
            <strong class="text-muted">Qarz yo'q (tenglashgan)</strong>
          </div>
        </div>

        <!-- Pay type selector — DB'dagi pay_type jadvalidan -->
        <div class="form-unit">
          <label>To'lov turi</label>
          <div class="paytype-grid">
            <label
              v-for="pt in payTypes"
              :key="pt.id"
              class="pt-option"
              :class="{
                active: payForm.pay_type_id === pt.id,
                'dollar-opt': pt.id === dollarPayTypeId,
              }"
            >
              <input type="radio" :value="pt.id" v-model="payForm.pay_type_id" />
              {{ payTypeIcon(pt.id, pt.name) }} {{ pt.name }}
              <small>{{ pt.id === dollarPayTypeId ? '(USD)' : "(so'm)" }}</small>
            </label>
          </div>
        </div>

        <!-- Sum input -->
        <div class="form-unit">
          <label>{{ isDollarSelected ? 'Summa (USD)' : "Summa (so'm)" }}</label>
          <input
            type="number"
            v-model.number="payForm.summa"
            min="0"
            :step="isDollarSelected ? 1 : 1000"
            class="form-input"
            :placeholder="isDollarSelected ? '0 USD' : '0 so\'m'"
          />
        </div>

        <!-- KONVERTATSIYA toggle (faqat dollar tanlanganda VA so'm qarz mavjud bo'lganda) -->
        <!-- Supplier uchun: balance > 0 = biz qarzdormiz (so'mda) -->
        <div v-if="isDollarSelected && payTarget.balance > 0" class="convert-block">
          <label class="conv-toggle">
            <input type="checkbox" v-model="payForm.convert_to_sum" />
            <span class="conv-text">
              💱 <strong>Konvertatsiya $→so'm</strong> — Dollar to'lov so'm qarzni kamaytirsin
            </span>
          </label>
          <div v-if="payForm.convert_to_sum && payForm.summa > 0" class="conv-info">
            <div>Kursi: <b>{{ formatMoney(currentRate) }}</b></div>
            <div>{{ payForm.summa }} $ × {{ formatMoney(currentRate) }} = <b class="positive">{{ formatMoney(payForm.summa * currentRate) }}</b> so'm qarz kamayadi</div>
            <div class="conv-cassa">📤 Kassadan: <b>{{ payForm.summa }} $</b> chiqadi</div>
          </div>
        </div>

        <!-- KONVERTATSIYA toggle (so'm tanlanganda VA dollar qarz mavjud bo'lganda) -->
        <!-- Supplier uchun: balance_dollar > 0 = biz dollarda qarzdormiz -->
        <div v-if="!isDollarSelected && payTarget.balance_dollar > 0" class="convert-block">
          <label class="conv-toggle">
            <input type="checkbox" v-model="payForm.convert_to_dollar" />
            <span class="conv-text">
              💱 <strong>Konvertatsiya so'm→$</strong> — So'm to'lov dollar qarzni kamaytirsin
            </span>
          </label>
          <div v-if="payForm.convert_to_dollar && payForm.summa > 0 && currentRate > 0" class="conv-info">
            <div>Kursi: <b>{{ formatMoney(currentRate) }}</b></div>
            <div>{{ formatMoney(payForm.summa) }} so'm ÷ {{ formatMoney(currentRate) }} = <b class="positive">{{ (payForm.summa / currentRate).toFixed(2) }} $</b> dollar qarz kamayadi</div>
            <div class="conv-cassa">📤 Kassadan: <b>{{ formatMoney(payForm.summa) }} so'm</b> chiqadi</div>
          </div>
        </div>

        <div class="hint" v-if="isDollarSelected && !payForm.convert_to_sum">
          ⚠️ DOLLAR qarz kamayadi (kassadan dollar chiqadi)
        </div>
        <div class="hint" v-else-if="isDollarSelected && payForm.convert_to_sum">
          ✅ SO'M qarz kamayadi (kassadan DOLLAR chiqadi)
        </div>
        <div class="hint" v-else-if="!isDollarSelected && payForm.convert_to_dollar">
          ✅ DOLLAR qarz kamayadi (kassadan SO'M chiqadi)
        </div>
        <div class="hint" v-else>
          ✅ SO'M qarz kamayadi
        </div>

        <div class="form-unit">
          <label>Izoh (ixtiyoriy)</label>
          <input type="text" v-model="payForm.comment" class="form-input" placeholder="Izoh..." />
        </div>

        <!-- QARZ QO'SHISH BLOKI (kassaga ta'sir qilmaydi) -->
        <div class="add-debt-block">
          <label class="ad-toggle">
            <input type="checkbox" v-model="payForm.add_debt_only" />
            <span>📝 <strong>Faqat qarz yozish</strong> — kassaga ta'sirsiz, qo'shimcha qarz</span>
          </label>

          <div v-if="payForm.add_debt_only" class="ad-fields">
            <div class="form-unit">
              <label>{{ isDollarSelected ? "Yangi qarz (USD)" : "Yangi qarz (so'm)" }}</label>
              <input
                type="number"
                v-model.number="payForm.qarz_summa"
                min="0"
                :step="isDollarSelected ? 1 : 1000"
                class="form-input"
                :placeholder="isDollarSelected ? '0 USD' : '0 so\'m'"
              />
            </div>
            <div class="form-unit">
              <label>Qarz izohi</label>
              <input type="text" v-model="payForm.qarz_comment" class="form-input" placeholder="Masalan: avans, kredit..." />
            </div>
            <div class="hint warn">
              💡 Bu summa <b>kassaga ta'sir qilmaydi</b> — qo'shimcha qarz yozadi
            </div>
          </div>
        </div>

        <div class="modal-actions">
          <button class="btn-cancel" @click="$bvModal.hide('modal-pay-supplier')">Bekor</button>
          <button class="btn-save" @click="submitPay" :disabled="paying || !canSubmit">
            <b-icon icon="check-all"></b-icon>
            {{ paying ? 'Saqlanmoqda...' : saveButtonLabel }}
          </button>
        </div>
      </div>
    </b-modal>

    <!-- TARIX MODAL — kirim/to'lov tarixi -->
    <b-modal id="modal-history-supplier" hide-footer size="xl" modal-class="modern-modal">
      <template #modal-title>
        <span v-if="historyTarget">
          <b-icon icon="clock-history"></b-icon>
          {{ historyTarget.name }} — tarix
        </span>
      </template>

      <div v-if="historyLoading" class="loading-state">Yuklanmoqda...</div>

      <div v-else-if="historyData" class="history-content">
        <!-- BALANS XULASA -->
        <div class="hist-summary">
          <div class="hs-cell" v-if="historyData.balance_sum">
            <span class="hs-label">SO'M qarz:</span>
            <strong :class="historyData.balance_sum > 0 ? 'negative' : 'positive'">
              {{ historyData.balance_sum > 0 ? '−' : '+' }}{{ formatMoney(Math.abs(historyData.balance_sum)) }}
            </strong>
          </div>
          <div class="hs-cell" v-if="historyData.balance_dollar">
            <span class="hs-label">DOLLAR qarz:</span>
            <strong :class="historyData.balance_dollar > 0 ? 'negative' : 'positive'">
              {{ historyData.balance_dollar > 0 ? '−' : '+' }}{{ formatDollar(Math.abs(historyData.balance_dollar)) }}
            </strong>
          </div>
          <div class="hs-cell">
            <span class="hs-label">Tarix:</span>
            <strong>{{ (historyData.entries || []).length }} ta yozuv</strong>
          </div>
        </div>

        <!-- TAB FILTERS -->
        <div class="hist-tabs">
          <button class="ht-btn" :class="{ active: historyFilter === 'all' }" @click="historyFilter = 'all'">
            📋 Hammasi ({{ (historyData.entries || []).length }})
          </button>
          <button class="ht-btn" :class="{ active: historyFilter === 'kirim' }" @click="historyFilter = 'kirim'">
            📦 Kirimlar ({{ historyCounts.kirim }})
          </button>
          <button class="ht-btn" :class="{ active: historyFilter === 'tolov' }" @click="historyFilter = 'tolov'">
            💰 To'lovlar ({{ historyCounts.tolov }})
          </button>
        </div>

        <!-- TARIX JADVALI -->
        <div class="hist-table-wrap">
          <table class="hist-table">
            <thead>
              <tr>
                <th>Sana</th>
                <th>Hujjat</th>
                <th>Yo'nalish</th>
                <th class="num">Summa</th>
                <th>Valyuta</th>
                <th>Izoh</th>
                <th>Detal</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="e in filteredHistory">
                <tr :key="e.id">
                  <td class="muted small">{{ formatTs(e.datetime) }}</td>
                  <td>
                    <span class="doc-pill" :class="docPillClass(e.doc_type)">
                      {{ docLabel(e.doc_type) }}
                    </span>
                    <span v-if="e.doc_id" class="doc-id">#{{ e.doc_id }}</span>
                  </td>
                  <td>
                    <span v-if="e.direction === 'qarz'" class="dir-pill dir-debt">+ QARZ</span>
                    <span v-else class="dir-pill dir-pay">− TO'LOV</span>
                  </td>
                  <td class="num bold" :class="e.is_dollar ? 'cur-dollar' : ''">
                    {{ e.direction === 'qarz' ? '+' : '−' }}{{ e.is_dollar ? formatDollar(e.summa) : formatMoney(e.summa) }}
                  </td>
                  <td>
                    <span v-if="e.is_dollar" class="cur-tag dollar">💲 {{ e.pay_type || 'Dollar' }}</span>
                    <span v-else-if="e.pay_type" class="cur-tag sum">₸ {{ e.pay_type }}</span>
                    <span v-else class="muted">—</span>
                  </td>
                  <td class="muted">{{ e.comment || '—' }}</td>
                  <td>
                    <button v-if="e.prixod" class="btn-detail" @click="toggleEntryDetail(e.id)">
                      {{ expandedEntry === e.id ? 'Yopish' : "Ko'rish" }}
                    </button>
                    <span v-else class="muted">—</span>
                  </td>
                </tr>
                <tr v-if="expandedEntry === e.id && e.prixod" :key="'detail-' + e.id">
                  <td colspan="7" class="prixod-detail">
                    <div class="pd-header">
                      <strong>Kirim #{{ e.prixod.number || e.prixod.id }}</strong>
                      <span>Sana: {{ formatTs(e.prixod.created_at) }}</span>
                      <span>Jami: {{ formatMoney(e.prixod.summa) }}</span>
                      <span v-if="e.prixod.skidka_summa">Chegirma: {{ formatMoney(e.prixod.skidka_summa) }}</span>
                      <span v-if="e.prixod.rasxod_summa">Rasxod: {{ formatMoney(e.prixod.rasxod_summa) }}</span>
                    </div>
                    <table class="pd-items">
                      <thead>
                        <tr>
                          <th>Mahsulot</th>
                          <th class="num">Soni</th>
                          <th class="num">Narx</th>
                          <th class="num">Summa</th>
                          <th class="num">Chakana</th>
                          <th class="num">Optom</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(it, i) in e.prixod.items" :key="i">
                          <td>{{ it.name }}</td>
                          <td class="num">{{ it.count }}</td>
                          <td class="num">{{ formatMoney(it.kontragent_price) }}</td>
                          <td class="num bold">{{ formatMoney(it.kontragent_summa) }}</td>
                          <td class="num muted">{{ formatMoney(it.chakana_price) }}</td>
                          <td class="num muted">{{ formatMoney(it.optom_price) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </template>
              <tr v-if="filteredHistory.length === 0">
                <td colspan="7" class="empty-row">{{ historyFilter === 'all' ? "Tarix bo'sh" : "Bu turdagi yozuvlar yo'q" }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  data() {
    return {
      filter: "",
      balanceFilter: "haqdor",  // default: biz qarzdor bo'lgan ta'minotchilar
      parent: 0,
      list: [],
      totals: null,
      totalCount: 0,
      page: 1,
      limit: 50,
      hasMore: false,
      loading: false,
      rule: { change: true },
      // To'lov modal
      payTarget: null,
      paying: false,
      payForm: {
        pay_type_id: null,
        summa: 0,
        comment: '',
        convert_to_sum: false,
        convert_to_dollar: false,
        add_debt_only: false,
        qarz_summa: 0,
        qarz_comment: '',
      },
      payTypes: [],
      kassaBalance: null,
      currentRate: 12000,
      // Tarix modal
      historyTarget: null,
      historyData: null,
      historyLoading: false,
      historyFilter: 'all',     // 'all' | 'kirim' | 'tolov'
      expandedEntry: null,
    };
  },
  computed: {
    filteredRows() {
      if (this.filter.length > 1) {
        const q = this.filter.toLowerCase();
        return this.list.filter(row =>
          (row.name && row.name.toLowerCase().includes(q)) ||
          (row.phone_number && row.phone_number.includes(this.filter))
        );
      }
      return this.list;
    },
    dollarPayTypeId() {
      if (!this.payTypes.length) return 3;
      const found = this.payTypes.find(p => {
        const n = String(p.name || '').toLowerCase();
        return n.includes('dollar') || n.includes('usd') || n.includes('$') || n === 'долл' || n.startsWith('доллар');
      });
      return found ? found.id : 3;
    },
    isDollarSelected() {
      return this.payForm.pay_type_id === this.dollarPayTypeId;
    },
    payTypeIcon() {
      return (id, name) => {
        const n = String(name || '').toLowerCase();
        if (id === this.dollarPayTypeId) return '💲';
        if (n.includes('plast') || n.includes('пласт') || n.includes('card')) return '💳';
        if (n.includes('shot')   || n.includes('шот')   || n.includes('перевод')) return '📲';
        if (n.includes('naqd')   || n.includes('нақд')  || n.includes('cash'))   return '💵';
        return '💰';
      };
    },
    canSubmit() {
      const hasPayment = Number(this.payForm.summa || 0) > 0;
      const hasDebtAdd = !!this.payForm.add_debt_only && Number(this.payForm.qarz_summa || 0) > 0;
      return hasPayment || hasDebtAdd;
    },
    saveButtonLabel() {
      const hasPayment = Number(this.payForm.summa || 0) > 0;
      const hasDebtAdd = !!this.payForm.add_debt_only && Number(this.payForm.qarz_summa || 0) > 0;
      if (hasPayment && hasDebtAdd) return "💾 To'lov + Qarz saqlash";
      if (hasDebtAdd && !hasPayment) return "📝 Qarzni saqlash";
      if (hasPayment) return "💵 To'lovni saqlash";
      return "Saqlash";
    },
    // Tarix filterlash (kirim/tolov/all)
    filteredHistory() {
      const list = (this.historyData && this.historyData.entries) || [];
      if (this.historyFilter === 'kirim') return list.filter(e => e.doc_type === 'Кирим');
      if (this.historyFilter === 'tolov') return list.filter(e => e.doc_type === 'Тўлов');
      return list;
    },
    historyCounts() {
      const list = (this.historyData && this.historyData.entries) || [];
      return {
        kirim: list.filter(e => e.doc_type === 'Кирим').length,
        tolov: list.filter(e => e.doc_type === 'Тўлов').length,
      };
    },
  },
  watch: {
    filter() {
      if (this._searchTimer) clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.page = 1;
        this.loadBalances(false);
      }, 400);
    },
  },
  mounted() {
    this.fetchPayTypes();
    this.loadBalances(false);
    this.loadKassaBalance();
    this.loadDollarRate();
  },
  methods: {
    formatMoney(n) {
      const v = parseFloat(n || 0);
      return v.toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + " so'm";
    },
    formatDollar(n) {
      const v = parseFloat(n || 0);
      return v.toLocaleString('ru-RU', { maximumFractionDigits: 2 }) + " $";
    },
    // Supplier konteksti (backend supplier formulasi bilan):
    //   balance > 0 → BIZ QARZDORMIZ (prixod qildik, hali to'lamadik) → 🔴 qizil
    //   balance < 0 → BIZGA QAYTADI (avans berdik, supplier qaytaradi) → 🟢 yashil
    //   balance = 0 → tenglashgan
    overallStatus(item) {
      const sumDebt    = (item.balance || 0) > 0;
      const dollarDebt = (item.balance_dollar || 0) > 0;
      const sumCredit    = (item.balance || 0) < 0;
      const dollarCredit = (item.balance_dollar || 0) < 0;
      if (sumDebt || dollarDebt) return 'sb-debt';      // biz qarzdormiz
      if (sumCredit || dollarCredit) return 'sb-credit'; // bizga qaytadi (avans)
      return 'sb-zero';
    },
    overallStatusLabel(item) {
      const c = this.overallStatus(item);
      return {
        'sb-debt': '🔴 Biz qarzdor',
        'sb-credit': '🟢 Bizga qaytadi',
        'sb-zero': '⚪ Tenglik',
      }[c] || '';
    },

    setBalanceFilter(v) {
      this.balanceFilter = v;
      this.page = 1;
      this.loadBalances(false);
    },

    // ============================================================
    // TARIX (history) — kirim/to'lov tarixi
    // ============================================================
    async openHistory(item) {
      this.historyTarget = item;
      this.historyData = null;
      this.historyLoading = true;
      this.historyFilter = 'all';
      this.expandedEntry = null;
      this.$bvModal.show('modal-history-supplier');
      try {
        // GET /api/v1/kontragent/supplier-balance/:id — entries[] qaytaradi
        const res = await axios.get('api/v1/kontragent/supplier-balance/' + item.id);
        // entries'dan tashqari qoldiq summasini ham olamiz (dual-balance'dan)
        const dualRes = await axios.get('api/v1/kontragent/dual-balance/' + item.id).catch(() => null);
        this.historyData = {
          ...res.data,
          balance_sum:    dualRes && dualRes.data && dualRes.data.sum ?    dualRes.data.sum.balance    : (res.data.debt || 0),
          balance_dollar: dualRes && dualRes.data && dualRes.data.dollar ? dualRes.data.dollar.balance : 0,
        };
      } catch (e) {
        console.error(e);
        alert("Tarixni yuklashda xato: " + (e.response?.data?.message || e.message));
      } finally {
        this.historyLoading = false;
      }
    },
    toggleEntryDetail(id) {
      this.expandedEntry = this.expandedEntry === id ? null : id;
    },
    formatTs(unix) {
      if (!unix) return '—';
      const d = new Date(unix * 1000);
      const pad = n => String(n).padStart(2, '0');
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    docLabel(t) {
      const map = {
        'Кирим':       'Kirim faktura',
        'Тўлов':       "To'lov",
        'Возврат поставщик': 'Vozvrat',
        'Kontragentinitial': 'Boshlang\'ich qoldiq',
      };
      return map[t] || t;
    },
    docPillClass(t) {
      if (t === 'Кирим') return 'doc-kirim';
      if (t === 'Тўлов') return 'doc-tolov';
      if (t === 'Возврат поставщик') return 'doc-refund';
      return 'doc-other';
    },

    async loadBalances(append) {
      this.loading = true;
      try {
        const params = {
          type: 2,                       // ta'minotchilar
          page:  this.page,
          limit: this.limit,
        };
        if (this.balanceFilter) params.status = this.balanceFilter;
        if (this.filter.length > 1) params.q = this.filter;

        const res = await axios.get('api/v1/kontragent/balances', { params });
        const data = res.data || {};
        const items = data.items || [];

        if (append) {
          this.list = this.list.concat(items);
        } else {
          this.list = items;
        }
        this.totalCount = (data.pagination && data.pagination.total) || items.length;
        this.hasMore = (this.list.length < this.totalCount);
        this.totals = (data.totals && data.totals.yetkazib_beruvchi) || null;
      } catch (e) {
        console.error('Balanslarni yuklashda xato:', e);
      } finally {
        this.loading = false;
      }
    },

    async loadMore() {
      this.page++;
      await this.loadBalances(true);
    },

    Data() {
      this.page = 1;
      this.loadBalances(false);
    },

    handleRowClick(item) {
      this.$router.push(`/malumot/taminotchi/update/${item.id}`);
    },

    trash(id) {
      if (!confirm(this.$t("Haqiqatan ham o'chirmoqchimisiz?"))) return;
      axios.delete(`api/v1/kontragent/id/${id}`).then(() => this.Data());
    },

    /* ─── Pay types ─── */
    async fetchPayTypes() {
      try {
        const res = await axios.get('api/v1/pay-type/');
        this.payTypes = (res.data || []).slice().sort((a, b) => a.id - b.id);
      } catch (e) {
        this.payTypes = [
          { id: 1, name: 'Naqd' },
          { id: 2, name: 'Plastik' },
          { id: 3, name: 'Dollar' },
          { id: 4, name: 'Shot' },
        ];
      }
    },

    async loadKassaBalance() {
      try {
        const res = await axios.get('api/v1/kontragent/kassa-balance');
        this.kassaBalance = res.data || null;
      } catch (e) { /* ignore */ }
    },
    async loadDollarRate() {
      try {
        const res = await axios.get('api/v1/dollar-exchange-rate/last');
        const r = Number((res.data && res.data.rate) || 0);
        if (r > 1) this.currentRate = r;
      } catch (e) { /* ignore */ }
    },

    /* ─── To'lov modal ─── */
    openPayModal(item) {
      this.payTarget = item;
      const sumDefault = (this.payTypes.find(p => p.id !== this.dollarPayTypeId) || { id: 1 }).id;
      let defaultType = sumDefault;
      // Default: agar dollar qarz bor va so'm qarz yo'q — Dollar to'lov tanlash
      if ((item.balance || 0) <= 0 && (item.balance_dollar || 0) > 0) {
        defaultType = this.dollarPayTypeId;
      }
      this.payForm = {
        pay_type_id: defaultType,
        summa: 0,
        comment: '',
        convert_to_sum: false,
        convert_to_dollar: false,
        add_debt_only: false,
        qarz_summa: 0,
        qarz_comment: '',
      };
      this.loadKassaBalance();
      this.$bvModal.show('modal-pay-supplier');
    },

    async submitPay() {
      const summa = Number(this.payForm.summa || 0);
      const qarzSumma = this.payForm.add_debt_only ? Number(this.payForm.qarz_summa || 0) : 0;
      if (!this.payTarget || (summa <= 0 && qarzSumma <= 0)) {
        alert("Summa yoki qarz kiriting");
        return;
      }

      const isDollar = this.payForm.pay_type_id === this.dollarPayTypeId;
      const ccy = isDollar ? '$' : "so'm";
      let confirmText = '';
      if (summa > 0) {
        confirmText += `${summa.toLocaleString('ru-RU')} ${ccy} to'lov`;
        if (this.payForm.convert_to_sum && isDollar) confirmText += ' (KONVERT $→so\'m qarzga)';
        if (this.payForm.convert_to_dollar && !isDollar) confirmText += ' (KONVERT so\'m→$ qarzga)';
        confirmText += '\n';
      }
      if (qarzSumma > 0) {
        confirmText += `+${qarzSumma.toLocaleString('ru-RU')} ${ccy} qarz qo'shiladi (kassa ta'sirsiz)\n`;
      }
      confirmText += `\nTa'minotchi: ${this.payTarget.name}`;
      if (!confirm(confirmText)) return;

      this.paying = true;
      try {
        await axios.post('api/v1/kontragent-pay', {
          datetime: Math.floor(Date.now() / 1000),
          sklad_id: parseInt(localStorage.getItem('sklad_id')) || 1,
          kontragent_id: this.payTarget.id,
          summa: summa,
          pay_type_id: this.payForm.pay_type_id,
          comment: this.payForm.comment || '',
          current_total: 0,
          dollar_rate: this.currentRate,
          convert_to_sum:    !!this.payForm.convert_to_sum,
          convert_to_dollar: !!this.payForm.convert_to_dollar,
          qarz_summa: qarzSumma,
          qarz_comment: this.payForm.qarz_comment || '',
        });
        this.$bvModal.hide('modal-pay-supplier');
        this.payTarget = null;
        await this.loadBalances(false);
        await this.loadKassaBalance();
        alert("✅ Saqlandi");
      } catch (e) {
        console.error(e);
        alert("Xatolik: " + (e.response?.data?.message || e.message));
      } finally {
        this.paying = false;
      }
    },
  }
};
</script>

<style scoped>
/* SAHIFA ASOSI */
.g-page {
  padding: 20px;
  background: #f8fafc;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.content-container {
  max-width: 1500px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* HEADER */
.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.page-title {
  color: #a142f4;
  font-weight: 800;
  font-size: 26px;
  margin: 0;
}

.header-actions { display: flex; align-items: center; gap: 12px; }

/* SEARCH */
.search-wrapper { position: relative; display: flex; align-items: center; }
.search-icon { position: absolute; left: 12px; color: #94a3b8; }

.modern-search {
  background: white;
  border: 1px solid #e2e8f0;
  padding: 8px 15px 8px 35px;
  border-radius: 12px;
  width: 250px;
  outline: none;
  transition: 0.3s;
}

.modern-search:focus {
  border-color: #a142f4;
  box-shadow: 0 0 0 4px rgba(161, 66, 244, 0.1);
}

/* TUGMALAR */
.add-btn {
  background: linear-gradient(135deg, #a142f4 0%, #8b2cf5 100%);
  color: white;
  border: none;
  height: 40px;
  padding: 0 20px;
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 15px rgba(161, 66, 244, 0.3);
}

.refresh-btn {
  background: white;
  border: 1px solid #e2e8f0;
  color: #64748b;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  cursor: pointer;
}

/* BALANCE FILTER ROW */
.balance-filter-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  background: white;
  border-radius: 14px;
  padding: 12px 16px;
  margin-bottom: 14px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.04);
}

.bf-btn {
  background: #f1f5f9;
  border: 2px solid transparent;
  color: #64748b;
  padding: 7px 16px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  transition: 0.2s;
}

.bf-btn:hover { background: #e2e8f0; }

.bf-btn.active {
  background: #faf5ff;
  border-color: #a142f4;
  color: #a142f4;
  box-shadow: 0 2px 8px rgba(161,66,244,0.12);
}

.balance-summary {
  display: flex;
  gap: 18px;
  margin-left: auto;
  align-items: center;
  flex-wrap: wrap;
}

.bs-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.bs-label {
  font-size: 10px;
  color: #94a3b8;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.bs-cell strong {
  font-size: 15px;
  font-weight: 800;
  font-family: 'Inter', system-ui, sans-serif;
}

.bs-positive { color: #dc2626; }
.bs-negative { color: #16a34a; }

/* TABLE */
.table-card {
  background: white;
  border-radius: 24px;
  padding: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
  border: 1px solid #f1f5f9;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.table-wrapper {
  overflow-y: auto;
  flex-grow: 1;
}

.custom-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.custom-table th {
  position: sticky;
  top: 0;
  background: white;
  z-index: 5;
  padding: 16px 14px;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  border-bottom: 1px solid #f1f5f9;
  text-align: left;
}

.custom-table th.num,
.custom-table td.num { text-align: right; }

.custom-table td {
  padding: 14px;
  border-bottom: 1px solid #f8fafc;
  vertical-align: middle;
  font-size: 14px;
  color: #334155;
}

.id-badge {
  background: #f1f5f9;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.font-bold { font-weight: 700; }
.text-muted { color: #94a3b8; }

/* BALANS BADGE */
.bal-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 800;
  font-family: 'Inter', system-ui, sans-serif;
  white-space: nowrap;
}

.bal-debt   { background: #fee2e2; color: #b91c1c; }   /* biz qarzdor */
.bal-credit { background: #dcfce7; color: #15803d; }   /* avans/qaytishi kerak */

/* STATUS PILL */
.status-pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

.sb-debt   { background: #fee2e2; color: #b91c1c; }
.sb-credit { background: #dcfce7; color: #15803d; }
.sb-zero   { background: #f1f5f9; color: #64748b; }

/* AMALLAR */
.actions {
  display: flex;
  gap: 6px;
  justify-content: center;
}

.icon-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;
  cursor: pointer;
}

.icon-btn.pay {
  background: #dcfce7;
  color: #15803d;
}
.icon-btn.pay:hover {
  background: #16a34a;
  color: white;
  transform: scale(1.05);
}

.icon-btn.edit {
  background: #eff6ff;
  color: #2563eb;
}
.icon-btn.edit:hover {
  background: #2563eb;
  color: white;
  transform: scale(1.1);
}

.icon-btn.del {
  background: #fff1f2;
  color: #e11d48;
}
.icon-btn.del:hover {
  background: #e11d48;
  color: white;
  transform: scale(1.1);
}

.table-footer {
  padding: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #f1f5f9;
  color: #64748b;
  font-size: 13px;
}

.btn-load-more {
  background: #faf5ff;
  border: 1px solid #e9d5ff;
  color: #a142f4;
  padding: 7px 16px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
}
.btn-load-more:hover { background: #f3e8ff; }

.empty-row {
  text-align: center;
  padding: 60px;
  color: #94a3b8;
  font-style: italic;
}

.spinner {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid #e9d5ff;
  border-top-color: #a142f4;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  vertical-align: middle;
  margin-right: 8px;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* PAY MODAL */
.pay-content { padding: 5px; }

.pay-balance-row {
  display: flex;
  gap: 14px;
  background: #f8fafc;
  padding: 12px 16px;
  border-radius: 12px;
  margin-bottom: 16px;
}

.pb-cell {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.pb-label {
  font-size: 10px;
  color: #94a3b8;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.pb-cell strong {
  font-size: 18px;
  font-weight: 800;
  font-family: 'Inter', system-ui, sans-serif;
  margin-top: 4px;
}

.positive { color: #dc2626; }
.negative { color: #16a34a; }

.paytype-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.pt-option {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 14px;
  cursor: pointer;
  font-weight: 700;
  font-size: 14px;
  color: #334155;
  transition: 0.2s;
  margin: 0;
}

.pt-option input[type="radio"] { display: none; }

.pt-option:hover { border-color: #cbd5e1; }

.pt-option.active {
  border-color: #a142f4;
  background: #faf5ff;
  color: #7c3aed;
  box-shadow: 0 2px 8px rgba(161,66,244,0.12);
}

.pt-option.dollar-opt.active {
  border-color: #16a34a;
  background: #f0fdf4;
  color: #15803d;
  box-shadow: 0 2px 8px rgba(22,163,74,0.12);
}

.pt-option small {
  font-size: 11px;
  font-weight: 600;
  opacity: 0.7;
}

.form-unit { margin-bottom: 14px; }

.form-unit label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  margin-bottom: 7px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.form-input {
  width: 100%;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 14px;
  color: #1e293b;
  font-weight: 600;
  font-size: 14px;
  outline: none;
  transition: 0.2s;
}

.form-input:focus {
  background: white;
  border-color: #a142f4;
  box-shadow: 0 0 0 3px rgba(161,66,244,0.08);
}

.hint {
  font-size: 11px;
  font-weight: 600;
  margin-top: 5px;
  padding: 6px 10px;
  border-radius: 8px;
  background: #f8fafc;
  color: #64748b;
}
.hint.warn { background: #fef3c7; color: #92400e; }

/* KASSA BALANSI */
.kassa-row {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border: 1px solid #6ee7b7;
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 14px;
}
.kr-title {
  font-size: 11px;
  font-weight: 800;
  color: #065f46;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}
.kr-cells { display: flex; gap: 14px; flex-wrap: wrap; }
.kr-cell { display: flex; flex-direction: column; }
.kr-cell.dollar strong { color: #15803d; }
.kr-label {
  font-size: 10px; color: #6b7280;
  font-weight: 700; text-transform: uppercase;
}
.kr-cell strong {
  font-size: 16px; font-weight: 800;
  color: #065f46; font-family: 'Inter', system-ui;
}

/* KONVERTATSIYA */
.convert-block {
  background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%);
  border: 1px solid #c4b5fd;
  border-radius: 10px;
  padding: 10px 14px;
  margin: -8px 0 8px 0;
}
.conv-toggle {
  display: flex; align-items: center; gap: 8px;
  cursor: pointer; font-size: 13px;
  color: #5b21b6; margin: 0;
}
.conv-toggle input { width: 18px; height: 18px; cursor: pointer; }
.conv-text strong { color: #4c1d95; }
.conv-info {
  background: white; border-radius: 8px;
  padding: 8px 12px; margin-top: 8px;
  font-size: 12px; color: #475569; line-height: 1.6;
}
.conv-info b { color: #1e293b; font-family: 'Inter', system-ui; }
.conv-info .positive { color: #16a34a; }
.conv-cassa {
  margin-top: 4px; padding-top: 4px;
  border-top: 1px dashed #cbd5e1;
  color: #15803d; font-weight: 600;
}

/* QARZ QO'SHISH */
.add-debt-block {
  margin-top: 14px; padding: 12px;
  background: #fff7ed;
  border: 1.5px dashed #fed7aa;
  border-radius: 10px;
}
.ad-toggle {
  display: flex; align-items: center; gap: 8px;
  cursor: pointer; font-size: 13px; color: #92400e; margin: 0;
}
.ad-toggle input { width: 18px; height: 18px; cursor: pointer; }
.ad-toggle strong { color: #78350f; }
.ad-fields { margin-top: 10px; }

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 14px;
  border-top: 1px solid #f1f5f9;
}

.btn-cancel {
  background: #f1f5f9;
  color: #64748b;
  border: none;
  padding: 9px 20px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
}

.btn-save {
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
  color: white;
  border: none;
  padding: 9px 22px;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(22,163,74,0.25);
}

.btn-save:disabled {
  background: #cbd5e1;
  box-shadow: none;
  cursor: not-allowed;
}

/* ============= ROW CLICK + HISTORY MODAL ============= */
.clickable-row { cursor: pointer; }
.clickable-row:hover { background: #f7f4ff !important; }

.loading-state { text-align: center; padding: 32px; color: #94a3b8; }

.history-content { padding: 4px; }
.hist-summary {
  display: flex; gap: 16px; flex-wrap: wrap;
  padding: 12px 16px; background: #f8fafc; border-radius: 8px; margin-bottom: 12px;
}
.hs-cell { display: flex; flex-direction: column; min-width: 140px; }
.hs-label { font-size: 11px; color: #64748b; text-transform: uppercase; }
.hs-cell strong { font-size: 16px; margin-top: 2px; }
.hs-cell .positive { color: #16a34a; }
.hs-cell .negative { color: #dc2626; }

.hist-tabs { display: flex; gap: 6px; margin-bottom: 10px; }
.ht-btn {
  padding: 6px 12px; background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 6px;
  font-size: 13px; cursor: pointer; transition: all 0.15s;
}
.ht-btn:hover { background: #e2e8f0; }
.ht-btn.active { background: #7c3aed; color: white; border-color: #7c3aed; }

.hist-table-wrap { max-height: 60vh; overflow: auto; border: 1px solid #e2e8f0; border-radius: 6px; }
.hist-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.hist-table th, .hist-table td { padding: 8px 10px; border-bottom: 1px solid #f1f5f9; text-align: left; }
.hist-table th { background: #f8fafc; font-weight: 600; font-size: 11px; text-transform: uppercase; color: #64748b; position: sticky; top: 0; }
.hist-table td.num { text-align: right; }
.hist-table td.bold { font-weight: 700; }
.hist-table td.muted { color: #94a3b8; }
.hist-table td.small { font-size: 12px; }

.doc-pill {
  display: inline-block; padding: 2px 8px; border-radius: 10px;
  font-size: 11px; font-weight: 600;
}
.doc-pill.doc-kirim  { background: #fee2e2; color: #991b1b; }
.doc-pill.doc-tolov  { background: #dcfce7; color: #166534; }
.doc-pill.doc-refund { background: #fef3c7; color: #92400e; }
.doc-pill.doc-other  { background: #e2e8f0; color: #475569; }
.doc-id { font-size: 11px; color: #94a3b8; margin-left: 4px; }

.dir-pill {
  display: inline-block; padding: 2px 8px; border-radius: 4px;
  font-size: 11px; font-weight: 700;
}
.dir-pill.dir-debt { background: #fee2e2; color: #991b1b; }
.dir-pill.dir-pay  { background: #dcfce7; color: #166534; }

.btn-detail {
  padding: 4px 10px; background: #ede9fe; color: #6d28d9;
  border: 1px solid #ddd6fe; border-radius: 4px; font-size: 12px; cursor: pointer;
}
.btn-detail:hover { background: #ddd6fe; }

.prixod-detail { background: #faf5ff !important; padding: 12px 16px !important; }
.pd-header {
  display: flex; gap: 16px; flex-wrap: wrap; padding-bottom: 8px;
  border-bottom: 1px solid #e9d5ff; margin-bottom: 8px; font-size: 13px;
}
.pd-items { width: 100%; border-collapse: collapse; font-size: 12px; }
.pd-items th, .pd-items td { padding: 4px 8px; border-bottom: 1px solid #e9d5ff; }
.pd-items th { background: #f3e8ff; text-align: left; font-weight: 600; }
.pd-items td.num { text-align: right; }

.hist-table td.cur-dollar { color: #15803d; }
.cur-tag {
  display: inline-block; padding: 2px 8px; border-radius: 4px;
  font-size: 11px; font-weight: 600;
}
.cur-tag.dollar { background: #dcfce7; color: #166534; }
.cur-tag.sum    { background: #dbeafe; color: #1e3a8a; }
</style>
